import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { showErrorToast, showSuccessToast } from "#app/notifications";
import { formatErrorWithLocation } from "#modules/materials/helpers/materialErrorFormatter";
import { materialsVolumetricPriceQuery } from "#modules/materials/infra/materialsVolumetricPrice.query";
import { uploadVolumetricBaseMaterials } from "#modules/materials/infra/uploadVolumetricBaseMaterials.api";
import { MaterialUploadResponse, MaterialUploadError } from "shared";

export const useUploadVolumetricBaseMaterials = (
  onError?: (errors: MaterialUploadError[]) => void,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, ...rest } = useMutation({
    mutationFn: uploadVolumetricBaseMaterials,
    onSuccess: () => {
      showSuccessToast({
        title: t("settings.materials.upload.successMessage"),
      });
      void queryClient.invalidateQueries(materialsVolumetricPriceQuery());
    },
    onError: (error: unknown) => {
      let uploadError: MaterialUploadResponse | null = null;

      if (error && typeof error === "object" && "success" in error && "errors" in error) {
        uploadError = error as MaterialUploadResponse;
      } else if (error instanceof Error) {
        for (const value of Object.values(error)) {
          if (value && typeof value === "object" && "success" in value && "errors" in value) {
            uploadError = value as MaterialUploadResponse;
            break;
          }
        }
      }

      if (
        uploadError &&
        uploadError.errors &&
        uploadError.errors.length > 0 &&
        uploadError.errors[0]
      ) {
        if (onError) {
          onError(uploadError.errors);
        }

        const primaryError = formatErrorWithLocation(uploadError.errors[0], t);

        if (uploadError.errors.length > 1) {
          const errorList = uploadError.errors
            .slice(0, 5)
            .map((e) => formatErrorWithLocation(e, t))
            .join("\n");

          showErrorToast({
            title: t("settings.materials.upload.validationErrors"),
            message: errorList,
            autoClose: 10000,
          });
        } else {
          showErrorToast({ title: primaryError });
        }
        return;
      }

      showErrorToast({
        title: t("settings.materials.upload.failedToUpload"),
      });
    },
  });

  return { uploadVolumetricBaseMaterials: mutate, ...rest };
};
